/**
 * VizCodeEditor - Monaco editor wrapper for JavaScript visualization code.
 *
 * Props:
 *   value        {string}   - Current code content
 *   onChange     {fn}       - Called with new string on change
 *   onExecute    {fn}       - Called on Ctrl+Enter
 *   onEditorMount {fn}      - Called with (editor, monaco) after mount
 *   height       {string}   - CSS height (default '400px')
 *   readOnly     {boolean}
 */

import { useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';

export default function VizCodeEditor({
  value = '',
  onChange,
  onExecute,
  onEditorMount,
  height = '400px',
  readOnly = false,
}) {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);

  const handleEditorDidMount = useCallback(
    (editor, monaco) => {
      editorRef.current = editor;
      monacoRef.current = monaco;

      // Ctrl+Enter to execute
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
        onExecute?.();
      });

      onEditorMount?.(editor, monaco);
    },
    [onExecute, onEditorMount]
  );

  return (
    <div style={{ height, width: '100%', border: '1px solid #374151', borderRadius: '4px', overflow: 'hidden' }}>
      <Editor
        height="100%"
        language="javascript"
        value={value}
        onChange={onChange}
        onMount={handleEditorDidMount}
        theme="vs-dark"
        options={{
          minimap: { enabled: false },
          lineNumbers: 'on',
          scrollBeyondLastLine: false,
          automaticLayout: true,
          fontSize: 13,
          tabSize: 2,
          wordWrap: 'on',
          readOnly,
          padding: { top: 10, bottom: 10 },
          renderLineHighlight: 'line',
          scrollbar: {
            vertical: 'auto',
            horizontal: 'auto',
          },
          suggestOnTriggerCharacters: false,
          quickSuggestions: { other: true, comments: false, strings: false },
        }}
      />
    </div>
  );
}
